import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookMarked, Clock, Loader2 } from 'lucide-react';
import { Card, CardContent } from "@/components/ui/card";

const RecentlyUpdated = () => {
  const { api } = useAuth();
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/stories/recently-updated?limit=5');
        setStories(res.data);
      } catch (e) {
        // Non-critical error suppressed
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [api]);

  if (loading || stories.length === 0) return null;

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const hrs = Math.floor(diff / 3600000);
    if (hrs < 1) return 'just now';
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  return (
    <Card className="border-brand-primary/10" data-testid="recently-updated">
      <CardContent className="p-5">
        <h3 className="font-serif text-lg font-semibold text-text-primary flex items-center gap-2 mb-4">
          <BookMarked className="w-5 h-5 text-brand-accent" />
          Recently Updated
        </h3>
        <div className="space-y-3">
          {stories.map((story) => {
            const chapterCount = (story.content?.match(/\[chapter:/g) || []).length;
            return (
              <Link
                key={story.id}
                to={`/story/${story.id}`}
                className="flex items-start gap-3 p-2.5 rounded-lg hover:bg-surface-alt transition-colors"
                data-testid={`updated-story-${story.id}`}
              >
                {story.cover_image ? (
                  <img
                    src={story.cover_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${story.cover_image}` : story.cover_image}
                    alt={story.title}
                    className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                    style={{ objectPosition: `center ${story.cover_image_position || 50}%` }}
                  />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
                    <BookMarked className="w-5 h-5 text-brand-primary/40" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-text-primary truncate">{story.title}</p>
                  <p className="text-xs text-text-muted mt-0.5">
                    by {story.author_name} {chapterCount > 0 && `\u00B7 ${chapterCount} chapters`}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <Clock className="w-3 h-3 text-text-muted" />
                    <span className="text-xs text-text-muted">Updated {timeAgo(story.updated_at)}</span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default RecentlyUpdated;
